import { Select } from 'antd';
import { useEffect, useState } from 'react';
import type { StoredUser } from '../../hooks/types';
import useMatchedUsers from '../../hooks/useMatchedUsers';
import useQueryUsersByIds from '../../hooks/useQueryUsersByIds';
import { compact, userFullName } from '../../lib/utils';
import { generateAntdFormItemId } from '../Teams/TeamEditor/util';

type Option = {
  readonly value: string;
  readonly label: string;
};

const userLabel = (matchedUser: {
  readonly first_name?: string;
  readonly last_name?: string;
  readonly user_type?: string;
  readonly user_name: string;
}): string => {
  return `${userFullName(matchedUser)} (${matchedUser.user_name}) ${
    matchedUser.user_type ?? ''
  }`.trim();
};

/**
 * Props for the Search User Select input component.
 */
type SearchUserSelectProps = {
  /**
   * The name of the current form
   */
  readonly formName: string;
  /**
   * The name of the current form list
   */
  readonly formListName?: string;
  /**
   * The field name of current form item
   */
  readonly fieldName: string;
  /**
   * Current users assigned to the team role
   */
  readonly currentUserIds?: readonly string[] | string;
  /**
   * Indicates if it is single selection.
   */
  readonly singleMode?: boolean;
} & {
  /**
   * Callback when the selected users change.
   */
  readonly onChange: (selectedOptions: readonly StoredUser[]) => void;
};

const minCharacterToStartSearch = 3;

/**
 * Renders a multi-select input box within Form.Item for searching PRAS users.
 */
const SearchUserSelect = ({
  formName,
  formListName,
  currentUserIds,
  fieldName,
  singleMode = false,
  onChange,
}: SearchUserSelectProps) => {
  const [open, setOpen] = useState(false);

  const [searchText, setSearchText] = useState('');

  const [selectedUserIds, setSelectedUserIds] = useState<
    readonly string[] | string
  >(currentUserIds !== undefined ? currentUserIds : []);

  const [userOptions, setUserOptions] = useState<readonly StoredUser[]>([]);

  const searchQuery =
    searchText.length < minCharacterToStartSearch ? '' : searchText;

  const matchedUsersBySearchText = useMatchedUsers(searchQuery);

  // This should allow deactivated users to show up if they are already associated with the shift
  // However, it will not let you search for them and assign them to a shift they are not already on.
  // A side effect of this, is that if you change the user for a shift, you won't be able to change
  // it back to the inactive user (although you could just cancel the dialog)
  const matchedUsersByIds = useQueryUsersByIds(selectedUserIds, {
    allowInactiveUsers: true,
  });

  useEffect(() => {
    const matchedUsersByIdsArray: readonly StoredUser[] =
      matchedUsersByIds.data !== undefined ? matchedUsersByIds.data.user : [];

    const matchedUsersBySearchTextArray: readonly StoredUser[] =
      matchedUsersBySearchText.data !== undefined
        ? matchedUsersBySearchText.data.search_user.filter(
            (user) =>
              !matchedUsersByIdsArray.map((user) => user.id).includes(user.id)
          )
        : [];

    /**
     * Put selected users at the end of options when mode is multiple.
     */
    const matchedUsers: readonly StoredUser[] = singleMode
      ? [...matchedUsersByIdsArray, ...matchedUsersBySearchTextArray]
      : [...matchedUsersBySearchTextArray, ...matchedUsersByIdsArray];

    setUserOptions(matchedUsers);
  }, [setUserOptions, matchedUsersByIds, matchedUsersBySearchText, singleMode]);

  const onUserSearch = (query: string) => {
    setSearchText(query);
  };

  const onUsersChange = (selectedOptions: readonly Option[]) => {
    const ids: readonly string[] = selectedOptions.map(({ value }) => value);
    setSelectedUserIds(ids);
    setOpen(false);
    setSearchText('');
    onChange(
      compact(ids.map((id) => userOptions.find((user) => user.id === id)))
    );
  };

  const selectInputId = generateAntdFormItemId([
    formName,
    formListName,
    fieldName,
  ]);

  return (
    <Select
      className="user-select"
      id={selectInputId}
      // role must be explicitly set here for the provision of valid aria attributes
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      role={'combobox'}
      // We set this attribute same as id for accessability
      aria-activedescendant={selectInputId}
      aria-expanded={open}
      aria-label="search-input"
      popupMatchSelectWidth={true}
      filterOption={false}
      mode={singleMode ? undefined : 'multiple'}
      notFoundContent={null}
      onChange={(_, options) => {
        onUsersChange(Array.isArray(options) ? options : [options]);
      }}
      onDropdownVisibleChange={setOpen}
      onSearch={onUserSearch}
      open={open}
      options={userOptions.map((matchedUser) => ({
        value: matchedUser.id,
        label: userLabel(matchedUser),
      }))}
      placeholder="Search for users"
      showSearch
      style={{ backgroundColor: 'transparent', width: '100%' }}
      value={selectedUserIds}
    />
  );
};

export default SearchUserSelect;
